/**
 * Places data hooks — ALL data access goes through the Hono backend
 * (apps/api/src/routes/places/places.ts) via the authed helpers in @/lib/api.
 * No supabase.from(...) / supabase.rpc(...) in this file by design: the places
 * tables carry RESTRICTIVE verify_backend_token() write policies, and reads
 * come from backend GET endpoints served with the per-request user-JWT client
 * (RLS enforced unchanged). See docs/BACKEND_API.md and the Places plan §1.3.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Geometry } from "geojson";
import {
  authedGet,
  authedPost,
  authedPatch,
  authedDelete,
} from "@/lib/api";
import { indexQueryOptions } from "@/hooks/lib/indexQueryOptions";
import { useOrganization } from "@/contexts/OrganizationContext";
import { toast } from "@/hooks/useToast";
import type {
  CreatePlaceInput,
  UpdatePlaceInput,
  DeletePlaceInput,
  TenureInput,
  UpsertPlaceUseInput,
  PlaceIndexRowWire,
  PlaceArtistHistoryRow,
} from "@releve/shared";

// ---------------------------------------------------------------------------
// Response shapes (defined by the backend routes)
// ---------------------------------------------------------------------------

export interface PlaceUse {
  id: string;
  is_primary: boolean;
  is_active: boolean;
  notes?: string | null;
  /** Canonical BEDES Occupancy Classification value (set via OccupancyClassificationSelect (materialized vocabulary)). */
  bedes_occupancy_classification: string | null;
}

export interface PlaceGisLocation {
  id: string;
  formatted_address: string | null;
  latitude: number | null;
  longitude: number | null;
  city: string | null;
  country_code: string | null;
  /**
   * Drawn GeoJSON (`gis_locations.geom`): a map pin `Point` or a drawn-area
   * `Polygon`. Only the detail endpoint selects it; NULL for address rows.
   */
  geom?: Geometry | null;
}

export interface PlaceListItem {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  gross_floor_area_m2: number | null;
  premises_level: "site" | "building" | "section";
  ownership_status: string | null;
  parent_place_id: string | null;
  external_source: string | null;
  place_uses: PlaceUse[];
  primary_use: PlaceUse | null;
  gis_location: PlaceGisLocation | null;
}

export interface PlaceTenure {
  id: string;
  place_id: string;
  tenure_type: string;
  valid_from: string;
  valid_to: string | null;
  cost: number | null;
  cost_period: "monthly" | "quarterly" | "annually" | null;
  currency: string | null;
  landlord_person_id: string | null;
  landlord?: {
    id: string;
    first_name: string;
    last_name: string;
    display_name: string | null;
  } | null;
  notes: string | null;
}

export interface PlaceChild {
  id: string;
  code: string;
  name: string;
  premises_level: "site" | "building" | "section";
  gross_floor_area_m2: number | null;
  is_active: boolean;
  /** Equal to the parent's while the sub-space still inherits its location. */
  gis_location_id: string | null;
  gis_location: Pick<PlaceGisLocation, "id" | "formatted_address" | "latitude" | "longitude" | "geom"> | null;
}

export interface PlaceDetail extends PlaceListItem {
  description: string | null;
  gis_location_id: string | null;
  gross_floor_area_source_value: number | null;
  gross_floor_area_source_unit: "m2" | "ft2" | null;
  // year_built / occupancy_pct / bedes_covered_area_type were consolidated
  // into the detail-record model (Phase 2 R3/R4) — see usePlaceProfile.ts.
  construction_status: "existing" | "project" | "test";
  ubid: string | null;
  notes: string | null;
  /** Place-level emission-region default (places_emissions). */
  emission_region_code: string | null;
  created_at: string;
  updated_at: string;
  parent: {
    id: string;
    code: string;
    name: string;
    premises_level: string;
    gis_location_id: string | null;
  } | null;
  children: PlaceChild[];
  tenures: PlaceTenure[];
  current_tenure: PlaceTenure | null;
}

export interface PlaceRollup {
  total_size_m2: number | null;
  own_size_m2: number | null;
  children_size_m2: number | null;
  subspace_count: number;
  project_count: number;
}

export type RollupScope = "self" | "with_children" | "children_only";

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * The COMPLETE org place index (`place_index` view, keyset-paged past
 * PostgREST's 1,000-row cap) — the places grid, pickers, and map view all
 * read this one query. The hierarchy DISPLAY order is applied by the grid
 * mapper (placeGridMapper.ts), not the server.
 *
 * Liveness contract: key `["places", orgId, "index", {}]` nests under the
 * prefix every place / tenure / use / definition mutation already
 * invalidates. ⚠️ One pinned dependency: the `gross_floor_area_m2` column
 * reaches this index ONLY because detail writes special-case
 * `invalidateGfaSurfaces` (usePlaceProfile.ts) — plain detail writes
 * deliberately do NOT invalidate ["places", orgId]. A future detail write
 * path that skips invalidateGfaSurfaces would stale this grid's size column
 * unnoticed (pinned by usePlaces.test.ts).
 */
export function usePlaceIndex(options?: { enabled?: boolean }) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  const queryOptions = indexQueryOptions<PlaceIndexRowWire>({
    entity: "places",
    organizationId: orgId,
    path: `/api/v1/places/orgs/${orgId}/places/index`,
  });
  return useQuery({
    ...queryOptions,
    // Callers can gate the fetch (the Places page skips it when the `places`
    // flag is off — flag-off orgs get the Addresses-only page, no extra call).
    enabled: queryOptions.enabled && (options?.enabled ?? true),
  });
}

/**
 * The org's effective offered occupancy vocabulary (Places-owned table with the
 * org's hide/order prefs applied — boundary doctrine: never a BEDES dictionary
 * read). Reference data: long staleTime.
 */
export function useOccupancyClassifications() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useQuery({
    queryKey: ["places", orgId, "occupancy-classifications"],
    enabled: !!orgId,
    staleTime: 10 * 60 * 1000,
    queryFn: () =>
      authedGet<{ value: string }[]>(
        `/api/v1/places/orgs/${orgId}/occupancy-classifications`,
      ),
  });
}

export function usePlaceByCode(code: string | undefined) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useQuery({
    queryKey: ["places", orgId, "detail", code],
    enabled: !!orgId && !!code,
    queryFn: () =>
      authedGet<PlaceDetail>(
        `/api/v1/places/orgs/${orgId}/places/by-code/${encodeURIComponent(code!)}`,
      ),
    // Re-opening the edit dialog (or revisiting the detail page) within a
    // few minutes serves from cache; place mutations invalidate
    // ["places", orgId] which prefix-matches this key.
    staleTime: 5 * 60 * 1000,
  });
}

export function usePlaceRollup(
  placeId: string | undefined,
  scope: RollupScope,
) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useQuery({
    queryKey: ["places", orgId, "rollup", placeId, scope],
    enabled: !!orgId && !!placeId,
    queryFn: () =>
      authedGet<PlaceRollup>(
        `/api/v1/places/orgs/${orgId}/places/${placeId}/rollup?scope=${scope}`,
      ),
    // docs/EXPANDED_ROWS.md §6.2 — this is summary data behind a grid row
    // expand, so repeatedly opening and closing a row must not refetch.
    // Place mutations invalidate ["places", orgId], which prefix-matches
    // this key, so a real change still lands immediately.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Artist / performer history for a place (M5.6): (person, project) pairs from
 * the get_place_artist_history RPC traversal, recent project runs first. No
 * artist-ness filter — project_team.role is free text; filtering is a UI
 * concern.
 */
export function usePlaceArtistHistory(placeId: string | undefined) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useQuery({
    queryKey: ["places", orgId, "artist-history", placeId],
    enabled: !!orgId && !!placeId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { rows } = await authedGet<{ rows: PlaceArtistHistoryRow[] }>(
        `/api/v1/places/orgs/${orgId}/places/${placeId}/artist-history`,
      );
      return rows;
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — every mutation invalidates the whole ['places', orgId] family:
// server-side triggers change children (inheritance/propagation/tenure cache),
// so targeted cache patching would leave stale descendants (plan risk #5).
// ---------------------------------------------------------------------------

interface CreatePlaceResult {
  place: { id: string; code: string; name: string };
  primary_use: unknown;
  tenure: unknown;
}

export function useCreatePlace() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useMutation({
    mutationFn: (input: CreatePlaceInput) =>
      authedPost<CreatePlaceResult>(
        `/api/v1/places/orgs/${orgId}/places`,
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["places", orgId] });
    },
    onError: (error: Error) => {
      toast.error("Failed to create place", { description: error.message });
    },
  });
}

export function useUpdatePlace() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useMutation({
    mutationFn: ({
      placeId,
      patch,
    }: {
      placeId: string;
      patch: UpdatePlaceInput;
    }) =>
      authedPatch<PlaceListItem>(
        `/api/v1/places/orgs/${orgId}/places/${placeId}`,
        patch,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["places", orgId] });
      // The projects index projects each project's linked places (`places`
      // jsonb + `search_text`): a renamed place changes `place_name` + `search_text`.
      void queryClient.invalidateQueries({ queryKey: ["projects", orgId] });
    },
    onError: (error: Error) => {
      toast.error("Failed to update place", { description: error.message });
    },
  });
}

export interface DeletePlaceResult {
  deleted_places: number;
  removed_project_links: number;
  removed_tenures: number;
  reassigned_children: number;
}

export function useDeletePlace() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useMutation({
    mutationFn: ({
      placeId,
      ...body
    }: { placeId: string } & DeletePlaceInput) =>
      authedPost<DeletePlaceResult>(
        `/api/v1/places/orgs/${orgId}/places/${placeId}/delete`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["places", orgId] });
      // The projects index projects each project's linked places (`places`
      // jsonb + `search_text`): a deleted place drops its project links.
      void queryClient.invalidateQueries({ queryKey: ["projects", orgId] });
    },
    onError: (error: Error) => {
      toast.error("Failed to delete place", { description: error.message });
    },
  });
}

export function useTenureMutations() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["places", orgId] });
  };
  const onError = (error: Error) => {
    toast.error("Failed to save tenure", { description: error.message });
  };

  const createTenure = useMutation({
    mutationFn: ({
      placeId,
      tenure,
    }: {
      placeId: string;
      tenure: TenureInput;
    }) =>
      authedPost<PlaceTenure>(
        `/api/v1/places/orgs/${orgId}/places/${placeId}/tenures`,
        tenure,
      ),
    onSuccess: invalidate,
    onError,
  });

  const updateTenure = useMutation({
    mutationFn: ({
      placeId,
      tenureId,
      tenure,
    }: {
      placeId: string;
      tenureId: string;
      tenure: TenureInput;
    }) =>
      authedPatch<PlaceTenure>(
        `/api/v1/places/orgs/${orgId}/places/${placeId}/tenures/${tenureId}`,
        tenure,
      ),
    onSuccess: invalidate,
    onError,
  });

  const closeTenure = useMutation({
    mutationFn: ({
      placeId,
      tenureId,
      validTo,
    }: {
      placeId: string;
      tenureId: string;
      validTo: string;
    }) =>
      authedPost<PlaceTenure>(
        `/api/v1/places/orgs/${orgId}/places/${placeId}/tenures/${tenureId}/close`,
        { valid_to: validTo },
      ),
    onSuccess: invalidate,
    onError,
  });

  return { createTenure, updateTenure, closeTenure };
}

export function usePlaceUseMutations() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["places", orgId] });
  };

  const upsertUse = useMutation({
    mutationFn: ({
      placeId,
      use,
    }: {
      placeId: string;
      use: UpsertPlaceUseInput;
    }) =>
      authedPost<PlaceUse>(
        `/api/v1/places/orgs/${orgId}/places/${placeId}/uses`,
        use,
      ),
    onSuccess: invalidate,
    onError: (error: Error) => {
      toast.error("Failed to save use", { description: error.message });
    },
  });

  const removeUse = useMutation({
    mutationFn: ({ placeId, useId }: { placeId: string; useId: string }) =>
      authedDelete<{ ok: boolean }>(
        `/api/v1/places/orgs/${orgId}/places/${placeId}/uses/${useId}`,
      ),
    onSuccess: invalidate,
    onError: (error: Error) => {
      toast.error("Failed to remove use", { description: error.message });
    },
  });

  return { upsertUse, removeUse };
}

// The hierarchy helpers (depth-first ordering, subtree filter) live in
// components/places/places-grid/placeGridMapper.ts — pure and unit-tested.
